import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusPill } from "@/components/StatusPill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient, formatMoney, auth } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { TrendingUp, DollarSign, ListChecks, BadgeCheck, Plus, Copy, UsersRound, Share2, ArrowRight, FileCheck2, FileClock, FileX2 } from "lucide-react";
import { toast } from "sonner";

const DocsCell = ({ status, count }) => {
  const label =
    status === "received" ? "Received" :
    status === "partial" ? `${count} attached` :
    "Pending";
  const cls =
    status === "received" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
    status === "partial"  ? "bg-amber-50  text-amber-700  border-amber-200"  :
    "bg-slate-50 text-slate-600 border-slate-200";
  const Icon =
    status === "received" ? FileCheck2 :
    status === "partial"  ? FileClock  :
    FileX2;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${cls}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <Card className="rounded-xl border-slate-200 shadow-sm p-6" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="mt-2 text-3xl font-heading font-medium text-slate-900 tracking-tight">{value}</div>
      </div>
      <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
  </Card>
);

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [recruits, setRecruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = auth.getUser();
  const referralCode = user?.referral_code || "";
  const referralUrl = referralCode ? `${window.location.origin}/apply?ref=${referralCode}` : "";

  useEffect(() => {
    if (auth.getRole() !== "partner") {
      navigate("/partner/login");
      return;
    }
    (async () => {
      try {
        const [s, r, rc] = await Promise.all([
          apiClient.get("/partner/stats"),
          apiClient.get("/referrals"),
          apiClient.get("/partner/recruits"),
        ]);
        setStats(s.data);
        setReferrals(r.data);
        setRecruits(rc.data);
      } catch (err) {
        toast.error("Could not load dashboard");
        if (err?.response?.status === 401) navigate("/partner/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const copyReferralLink = async () => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  return (
    <DashboardShell
      role="partner"
      title="Your referrals"
      subtitle="Track pipeline status, estimated commissions, and payouts."
      actions={
        <Link to={referralCode ? `/apply?ref=${referralCode}` : "/apply"}>
          <Button data-testid={TIDS.partnerNavSubmit} className="rounded-full bg-blue-600 hover:bg-blue-700 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
            <Plus className="h-4 w-4 mr-1" /> Submit a referral
          </Button>
        </Link>
      }
    >
      <div data-testid={TIDS.partnerDashboard} className="space-y-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard
            icon={DollarSign}
            label="Unpaid commission"
            value={formatMoney(stats?.estimated_unpaid ?? 0)}
            accent="bg-amber-50 text-amber-700"
            testid={TIDS.partnerStatsUnpaid}
          />
          <StatCard
            icon={BadgeCheck}
            label="Paid commission"
            value={formatMoney(stats?.paid_commission ?? 0)}
            accent="bg-emerald-50 text-emerald-700"
            testid={TIDS.partnerStatsPaid}
          />
          <StatCard
            icon={ListChecks}
            label="Total referrals"
            value={stats?.total_referrals ?? 0}
            accent="bg-blue-50 text-blue-700"
            testid={TIDS.partnerStatsTotal}
          />
          <StatCard
            icon={TrendingUp}
            label="Funded deals"
            value={stats?.funded_count ?? 0}
            accent="bg-indigo-50 text-indigo-700"
            testid={TIDS.partnerStatsFunded}
          />
        </div>

        {/* Referral link card */}
        <Card
          data-testid={TIDS.partnerReferralLinkCard}
          className="rounded-xl border-slate-200 shadow-sm overflow-hidden"
        >
          <div className="grid md:grid-cols-3">
            <div className="bg-gradient-to-br from-blue-600 to-emerald-600 text-white p-6 md:p-8 flex flex-col justify-between">
              <div>
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
                  <Share2 className="h-5 w-5" />
                </span>
                <h2 className="mt-5 text-2xl font-heading font-medium tracking-tight leading-tight">
                  Share your link.<br />
                  <span className="text-white/80">Earn on every funded deal.</span>
                </h2>
              </div>
              <div className="mt-6 flex items-center gap-2 text-sm">
                <UsersRound className="h-4 w-4" />
                <span data-testid={TIDS.partnerRecruitsCount}>
                  {recruits.length} application{recruits.length === 1 ? "" : "s"} attributed
                </span>
              </div>
            </div>
            <div className="md:col-span-2 p-6 md:p-8 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Your referral link</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <code className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 font-mono overflow-x-auto whitespace-nowrap">
                    {referralUrl || "Loading…"}
                  </code>
                  <Button
                    onClick={copyReferralLink}
                    data-testid={TIDS.partnerReferralCopyBtn}
                    className="rounded-full bg-slate-900 hover:bg-slate-800 text-white shrink-0"
                  >
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Share this with business owners who need capital. Anyone who submits an application through your link is automatically attributed to you — and you earn the tiered commission when their deal funds.
                </p>
              </div>

              {recruits.length > 0 ? (
                <div>
                  <div className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2">Recently recruited</div>
                  <ul className="space-y-2">
                    {recruits.slice(0, 4).map((r) => (
                      <li key={r.id} className="flex items-center justify-between text-sm rounded-lg border border-slate-200 px-3 py-2">
                        <div>
                          <div className="font-medium text-slate-900">{r.full_name}</div>
                          {r.company_name ? <div className="text-xs text-slate-500">{r.company_name}</div> : null}
                        </div>
                        <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </Card>

        <Card className="rounded-xl border-slate-200 shadow-sm overflow-hidden" data-testid={TIDS.partnerReferralsCard}>
          <div className="flex items-center justify-between p-6 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-medium text-slate-900">Your referrals</h2>
              <p className="text-sm text-slate-500">{referrals.length} total · document status shown per client</p>
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500">Loading…</div>
          ) : referrals.length === 0 ? (
            <div className="p-12 text-center">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                <Plus className="h-6 w-6" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-slate-900">No referrals yet</h3>
              <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">Share your link or submit a client to see them here.</p>
              <Link to={referralCode ? `/apply?ref=${referralCode}` : "/apply"}>
                <Button className="mt-6 rounded-full bg-blue-600 hover:bg-blue-700">Submit a referral</Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Business</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead className="text-right">Requested</TableHead>
                  <TableHead className="text-right">Commission</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((r) => (
                  <TableRow key={r.id} data-testid={TIDS.referralRow}>
                    <TableCell className="font-medium text-slate-900">{r.business_name}</TableCell>
                    <TableCell><StatusPill status={r.status} /></TableCell>
                    <TableCell>
                      <DocsCell status={r.documents_status} count={r.document_count} />
                    </TableCell>
                    <TableCell className="text-right text-slate-700">{formatMoney(r.funding_amount_needed)}</TableCell>
                    <TableCell className="text-right">
                      {r.commission_amount ? (
                        <span className={r.commission_paid ? "text-emerald-700 font-medium" : "text-slate-900 font-medium"}>
                          {formatMoney(r.commission_amount)}
                          {r.commission_paid ? <span className="ml-2 text-xs text-emerald-700">paid</span> : null}
                        </span>
                      ) : (
                        <span className="text-slate-500">{formatMoney(r.estimated_commission)} <span className="text-xs">est</span></span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Link to={`/partner/referrals/${r.id}`}>
                        <Button variant="ghost" size="sm" data-testid={TIDS.referralOpen} className="hover:text-blue-700">
                          Open <ArrowRight className="ml-1 h-4 w-4" />
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
